import React, { useState, useEffect } from 'react';
import { Plus, Copy, ExternalLink, Trash2, Inbox, Megaphone, Power } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const SubmissionsDialog = ({ page, onClose }) => {
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    if (page) api.get(`/landing-pages/${page.id}/submissions`).then((r) => setSubs(r.data)).catch(() => setSubs([]));
  }, [page]);
  if (!page) return null;
  return (
    <Dialog open={!!page} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase flex items-center gap-2"><Inbox className="w-5 h-5 text-primary" /> Leads captados — {page.title}</DialogTitle></DialogHeader>
        {!subs ? <p className="text-sm text-muted-foreground py-6 text-center">Carregando...</p> : subs.length === 0 ? (
          <p className="text-sm text-muted-foreground/60 py-8 text-center">Nenhum lead captado ainda. Divulgue o link da página!</p>
        ) : (
          <div className="space-y-2.5" data-testid="lp-submissions-list">
            {subs.map((s) => (
              <div key={s.id} className="rounded-lg border border-border/70 p-3.5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-semibold text-sm">{s.full_name} <span className="text-muted-foreground font-normal">· {s.phone}{s.email ? ` · ${s.email}` : ''}</span></p>
                  <span className="text-[10px] text-muted-foreground/70">{fmtDT(s.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-muted/50 border border-border">Entrada: {s.down_payment || '—'}</span>
                  <span className="px-2 py-0.5 rounded-full bg-muted/50 border border-border">Doc: {s.document_type || '—'}</span>
                  <span className="px-2 py-0.5 rounded-full bg-muted/50 border border-border">Já financiado: {s.has_financed || '—'}</span>
                  <span className="px-2 py-0.5 rounded-full bg-muted/50 border border-border">Compra: {s.purchase_when || '—'}</span>
                  <span className={`px-2 py-0.5 rounded-full border ${s.contact_channel === 'WhatsApp' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : 'bg-sky-500/10 text-sky-400 border-sky-500/25'}`}>{s.contact_channel || '—'}</span>
                  {s.lead_id && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25">BDC: {s.lead_id}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function LandingPages() {
  const { user } = useAuth();
  const isAdmin = ['Administrator', 'Owner'].includes(user?.role);
  const [pages, setPages] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '' });
  const [saving, setSaving] = useState(false);
  const [subsPage, setSubsPage] = useState(null);
  const [delPage, setDelPage] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/landing-pages'); setPages(data); }
    catch (e) { if (e.response?.status !== 403) toast({ title: 'Erro ao carregar', description: e.response?.data?.detail || '' }); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]); // eslint-disable-line

  if (!isAdmin) {
    return (
      <div className="text-center py-20 text-muted-foreground" data-testid="lp-no-access">
        <p className="font-semibold text-foreground">Sem permissão</p>
        <p className="text-sm mt-1">O módulo Landing Pages é exclusivo para administradores.</p>
      </div>
    );
  }

  const publicUrl = (p) => `${window.location.origin}/lp/${p.slug}`;

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(publicUrl(p));
      toast({ title: 'Link copiado!', description: publicUrl(p) });
    } catch (e) {
      toast({ title: 'Não foi possível copiar', description: publicUrl(p) });
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/landing-pages/${p.id}`, { active: !p.active });
      toast({ title: p.active ? 'Página desativada' : 'Página ativada' });
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post('/landing-pages', form);
      toast({ title: 'Landing page criada!' });
      setCreateOpen(false);
      setForm({ title: '', slug: '' });
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/landing-pages/${delPage.id}`);
      toast({ title: 'Landing page excluída' });
      setDelPage(null);
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-5" data-testid="lp-admin-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">Crie páginas públicas de captação. Cada lead enviado pelo formulário entra automaticamente no <b className="text-foreground">BDC como Novo Lead</b>.</p>
        <button data-testid="create-landing-page-button" onClick={() => setCreateOpen(true)} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nova landing page</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="landing-pages-table">
        {pages.map((p) => (
          <div key={p.id} className="rounded-xl border border-border bg-card p-5 glow-border card-hover">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center"><Megaphone className="w-4.5 h-4.5 text-primary" /></div>
                <div>
                  <h3 className="font-display font-bold leading-tight">{p.title}</h3>
                  <p className="text-[11px] text-muted-foreground font-mono">/lp/{p.slug}</p>
                </div>
              </div>
              <button data-testid={`lp-toggle-${p.slug}`} onClick={() => toggleActive(p)} title={p.active ? 'Desativar' : 'Ativar'} className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${p.active ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/25' : 'text-muted-foreground bg-muted/40 border-border'}`}>
                <Power className="w-3 h-3" /> {p.active ? 'ATIVA' : 'PAUSADA'}
              </button>
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5 text-primary" /><b className="text-foreground">{p.submissions}</b> leads captados</span>
              <span className="ml-auto text-[10px]">{p.created_by} · {fmtDT(p.created_at)}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-4 pt-4 border-t border-border/70">
              <button data-testid={`lp-copy-${p.slug}`} onClick={() => copyLink(p)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-border text-foreground/75 hover:text-foreground hover:border-primary/50 transition-colors"><Copy className="w-3.5 h-3.5" /> Copiar link</button>
              <a data-testid={`lp-open-${p.slug}`} href={publicUrl(p)} target="_blank" rel="noreferrer" className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-primary/60 transition-colors" title="Abrir página"><ExternalLink className="w-4 h-4" /></a>
              <button data-testid={`lp-subs-${p.slug}`} onClick={() => setSubsPage(p)} className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-primary/60 transition-colors" title="Ver leads"><Inbox className="w-4 h-4" /></button>
              <button data-testid={`lp-del-${p.slug}`} onClick={() => setDelPage(p)} className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-red-400/60 hover:text-red-400 hover:border-red-400/60 transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {pages.length === 0 && <div className="text-center py-16 text-muted-foreground">Nenhuma landing page criada ainda.</div>}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Nova landing page</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Título</label><input data-testid="lp-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Financiamento — Campanha Instagram" /></div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Endereço (slug)</label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground/70 font-mono">/lp/</span>
                <input data-testid="lp-slug-input" className={`${inputCls} font-mono lowercase`} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="financiamento-instagram" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/70">A página usa o modelo de <b>Aplicação de Financiamento</b> (captação de lead → BDC).</p>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="save-landing-page-button" onClick={create} disabled={saving || !form.title || !form.slug} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50">{saving ? 'Criando...' : 'Criar página'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delPage} onOpenChange={(o) => !o && setDelPage(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase text-red-400">Excluir landing page?</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/80">A página <b>{delPage?.title}</b> (/lp/{delPage?.slug}) sairá do ar. Os leads já captados permanecem no BDC.</p>
          <DialogFooter>
            <button onClick={() => setDelPage(null)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="lp-delete-confirm" onClick={confirmDelete} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition-colors">Excluir</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SubmissionsDialog page={subsPage} onClose={() => setSubsPage(null)} />
    </div>
  );
}
